import React, { createContext, useContext, useReducer, useRef, useCallback, useEffect } from 'react';
import { AudioFile, AudioPlayerState, AudioPlayerContextType } from '../types';
import { getAssetUrl } from '../config/environment';
import { audioService } from '../services/api';

// Queue persistence helpers
const QUEUE_STORAGE_KEY = 'naatcity-queue';
const CURRENT_AUDIO_STORAGE_KEY = 'naatcity-current-audio';
const PLAYBACK_STATE_KEY = 'naatcity-playback-state';

const saveQueueToStorage = (queue: AudioFile[]) => {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.warn('Failed to save queue to localStorage:', error);
  }
};

const loadQueueFromStorage = (): AudioFile[] => {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.warn('Failed to load queue from localStorage:', error);
    return [];
  }
};

const saveCurrentAudioToStorage = (currentAudio: AudioFile | null) => {
  try {
    if (currentAudio) {
      localStorage.setItem(CURRENT_AUDIO_STORAGE_KEY, JSON.stringify(currentAudio));
    } else {
      localStorage.removeItem(CURRENT_AUDIO_STORAGE_KEY);
    }
  } catch (error) {
    console.warn('Failed to save current audio to localStorage:', error);
  }
};

const loadCurrentAudioFromStorage = (): AudioFile | null => {
  try {
    const stored = localStorage.getItem(CURRENT_AUDIO_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to load current audio from localStorage:', error);
    return null;
  }
};

interface PlaybackState {
  audioId?: number;
  isPlaying: boolean;
  currentTime: number;
  volume: number;
  duration: number;
}

const savePlaybackStateToStorage = (state: Partial<PlaybackState>, audioId?: number) => {
  try {
    const existing = loadPlaybackStateFromStorage();
    const updated = { ...existing, ...state };
    if (audioId !== undefined) {
      updated.audioId = audioId;
    }
    localStorage.setItem(PLAYBACK_STATE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.warn('Failed to save playback state to localStorage:', error);
  }
};

const loadPlaybackStateFromStorage = (): PlaybackState => {
  try {
    const stored = localStorage.getItem(PLAYBACK_STATE_KEY);
    return stored ? JSON.parse(stored) : { isPlaying: false, currentTime: 0, volume: 1, duration: 0 };
  } catch (error) {
    console.warn('Failed to load playback state from localStorage:', error);
    return { isPlaying: false, currentTime: 0, volume: 1, duration: 0 };
  }
};

const clearPlaybackStateFromStorage = () => {
  try {
    localStorage.removeItem(PLAYBACK_STATE_KEY);
  } catch (error) {
    console.warn('Failed to clear playback state from localStorage:', error);
  }
};


// Initialize queue and ensure current audio is NOT in the queue
const initializeQueueWithCurrentAudio = (): { queue: AudioFile[]; currentAudio: AudioFile | null } => {
  const storedQueue = loadQueueFromStorage();
  const storedCurrentAudio = loadCurrentAudioFromStorage();
  
  if (storedCurrentAudio) {
    // Remove current audio from queue if it exists there (it should only be in "Now Playing")
    const filteredQueue = storedQueue.filter(audio => audio.id !== storedCurrentAudio.id);
    if (filteredQueue.length !== storedQueue.length) {
      // Queue was modified, save the updated queue
      saveQueueToStorage(filteredQueue);
    }
    return { queue: filteredQueue, currentAudio: storedCurrentAudio };
  }
  
  return { queue: storedQueue, currentAudio: storedCurrentAudio };
};

const { queue: initialQueue, currentAudio: initialCurrentAudio } = initializeQueueWithCurrentAudio();
const initialPlaybackState = loadPlaybackStateFromStorage();

const initialState: AudioPlayerState = {
  currentAudio: initialCurrentAudio,
  isPlaying: false, // Will be set to true after audio loads if needed
  currentTime: initialPlaybackState.currentTime,
  duration: initialPlaybackState.duration,
  volume: initialPlaybackState.volume,
  isLoading: false,
  playlist: [],
  currentIndex: -1,
  queue: initialQueue,
  isQueueOpen: false,
  playHistory: [],
  playlistSource: 'regular',
};

type AudioPlayerAction =
  | { type: 'SET_AUDIO'; payload: { audio: AudioFile; playlist?: AudioFile[]; source?: 'queue' | 'favorites' | 'regular' } }
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'SET_CURRENT_TIME'; payload: number }
  | { type: 'SET_DURATION'; payload: number }
  | { type: 'SET_VOLUME'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'NEXT_TRACK' }
  | { type: 'PREVIOUS_TRACK' }
  | { type: 'ADD_TO_QUEUE'; payload: AudioFile }
  | { type: 'REMOVE_FROM_QUEUE'; payload: number }
  | { type: 'CLEAR_QUEUE' }
  | { type: 'PLAY_NEXT'; payload: AudioFile }
  | { type: 'TOGGLE_QUEUE' }
  | { type: 'STOP_PLAYBACK' };

const audioPlayerReducer = (state: AudioPlayerState, action: AudioPlayerAction): AudioPlayerState => {
  switch (action.type) {
    case 'SET_AUDIO':
      const playlist = action.payload.playlist || [action.payload.audio];
      const currentIndex = playlist.findIndex(audio => audio.id === action.payload.audio.id);

      // Check if we're trying to play the same audio that's already playing
      const isSameAudio = state.currentAudio && state.currentAudio.id === action.payload.audio.id;

      saveCurrentAudioToStorage(action.payload.audio);

      // If it's a different audio, reset the playback position
      if (!isSameAudio) {
        savePlaybackStateToStorage({ currentTime: 0, isPlaying: true }, action.payload.audio.id);
      }

      // Handle queue based on the source
      let updatedQueue = state.queue;
      if (action.payload.source === 'queue') {
        // Playing from queue, keep queue as is
        updatedQueue = state.queue;
      } else if (action.payload.source === 'favorites') {
        // Playing from favorites, don't modify the queue at all
        updatedQueue = state.queue;
      } else {
        // Playing from elsewhere (regular), add to queue if not already there
        const isInQueue = state.queue.some(item => item.id === action.payload.audio.id);
        if (!isInQueue) {
          // Add current audio to the end of the queue
          updatedQueue = [...state.queue, action.payload.audio];
          saveQueueToStorage(updatedQueue);
        }
        // If playlist is provided, add all to queue
        if (action.payload.playlist) {
          action.payload.playlist.forEach(audio => {
            if (!updatedQueue.some(item => item.id === audio.id)) {
              updatedQueue.push(audio);
            }
          });
          saveQueueToStorage(updatedQueue);
        }
      }

      return {
        ...state,
        currentAudio: action.payload.audio,
        playlist,
        currentIndex,
        isLoading: !isSameAudio, // Don't set loading if it's the same audio
        playlistSource: action.payload.source || 'regular',
        queue: updatedQueue,
      };
    case 'PLAY':
      savePlaybackStateToStorage({ isPlaying: true }, state.currentAudio?.id);
      return { ...state, isPlaying: true };
    case 'PAUSE':
      savePlaybackStateToStorage({ isPlaying: false }, state.currentAudio?.id);
      return { ...state, isPlaying: false };
    case 'SET_CURRENT_TIME':
      // Save to storage every 2 seconds to avoid excessive writes
      if (Math.floor(action.payload) % 2 === 0 && Math.floor(action.payload) !== Math.floor(state.currentTime)) {
        savePlaybackStateToStorage({ currentTime: action.payload }, state.currentAudio?.id);
      }
      return { ...state, currentTime: action.payload };
    case 'SET_DURATION':
      savePlaybackStateToStorage({ duration: action.payload });
      return { ...state, duration: action.payload, isLoading: false };
    case 'SET_VOLUME':
      savePlaybackStateToStorage({ volume: action.payload });
      return { ...state, volume: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'NEXT_TRACK':
      if (state.currentIndex < state.playlist.length - 1) {
        const nextIndex = state.currentIndex + 1;
        const nextAudio = state.playlist[nextIndex];
        saveCurrentAudioToStorage(nextAudio);
        return {
          ...state,
          currentAudio: nextAudio,
          currentIndex: nextIndex,
          currentTime: 0,
          isLoading: true,
        };
      } else {
        // No more tracks to play, stop and reset but keep current audio visible
        return {
          ...state,
          isPlaying: false,
          currentTime: 0,
          isLoading: false,
        };
      }
    case 'PREVIOUS_TRACK':
      if (state.currentIndex > 0) {
        const prevIndex = state.currentIndex - 1;
        const prevAudio = state.playlist[prevIndex];
        saveCurrentAudioToStorage(prevAudio);
        return {
          ...state,
          currentAudio: prevAudio,
          currentIndex: prevIndex,
          currentTime: 0,
          isLoading: true,
        };
      }
      return state;
    case 'ADD_TO_QUEUE':
      // Check if the audio is already in the queue
      const isAlreadyInQueue = state.queue.some(item => item.id === action.payload.id);
      if (isAlreadyInQueue) {
        // Don't add if already in queue
        return state;
      }
      const newQueueAdd = [...state.queue, action.payload];
      saveQueueToStorage(newQueueAdd);
      return {
        ...state,
        queue: newQueueAdd,
      };
    case 'REMOVE_FROM_QUEUE':
      const removedAudio = state.queue[action.payload];
      const newQueueRemove = state.queue.filter((_, index) => index !== action.payload);
      saveQueueToStorage(newQueueRemove);

      // Check if we're removing the currently playing audio
      if (state.currentAudio && removedAudio && state.currentAudio.id === removedAudio.id) {
        // If there are other items in the queue, play the next one
        if (newQueueRemove.length > 0) {
          const nextIndex = Math.min(action.payload, newQueueRemove.length - 1);
          const nextAudio = newQueueRemove[nextIndex];
          saveCurrentAudioToStorage(nextAudio);
          return {
            ...state,
            queue: newQueueRemove,
            currentAudio: nextAudio,
            playlist: newQueueRemove,
            currentIndex: nextIndex,
            currentTime: 0,
            isLoading: true,
          };
        } else {
          // No more items in queue, stop playback
          saveCurrentAudioToStorage(null);
          return {
            ...state,
            queue: newQueueRemove,
            currentAudio: null,
            isPlaying: false,
            currentTime: 0,
            duration: 0,
            playlist: [],
            currentIndex: -1,
          };
        }
      }

      // If we're not removing the current audio, just update the queue
      return {
        ...state,
        queue: newQueueRemove,
      };
    case 'CLEAR_QUEUE':
      saveQueueToStorage([]);
      saveCurrentAudioToStorage(null);
      return {
        ...state,
        queue: [],
        currentAudio: null,
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        playlist: [],
        currentIndex: -1,
        isLoading: false,
      };
    case 'PLAY_NEXT':
      const newQueuePlayNext = [action.payload, ...state.queue];
      saveQueueToStorage(newQueuePlayNext);
      return {
        ...state,
        queue: newQueuePlayNext,
      };
    case 'TOGGLE_QUEUE':
      return {
        ...state,
        isQueueOpen: !state.isQueueOpen,
      };
    case 'STOP_PLAYBACK':
      saveCurrentAudioToStorage(null);
      clearPlaybackStateFromStorage();
      return {
        ...state,
        currentAudio: null,
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        playlist: [],
        currentIndex: -1,
        isLoading: false,
      };
    default:
      return state;
  }
};

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};

interface AudioPlayerProviderProps {
  children: React.ReactNode;
}

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(audioPlayerReducer, initialState);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const trackedPlayRef = useRef<number | null>(null);
  const restoredRef = useRef<boolean>(false); // Track which audio has been counted
  const stateRef = useRef(state); // Keep reference to current state

  // Update state ref whenever state changes
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();
    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      dispatch({ type: 'SET_DURATION', payload: audio.duration });
    };

    const handleTimeUpdate = () => {
      dispatch({ type: 'SET_CURRENT_TIME', payload: audio.currentTime });
    };

    const handleEnded = () => {
      // Automatically play next track when current ends
      dispatch({ type: 'NEXT_TRACK' });
    };

    const handleLoadStart = () => {
      dispatch({ type: 'SET_LOADING', payload: true });
    };

    const handleCanPlay = () => {
      dispatch({ type: 'SET_LOADING', payload: false });
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, []);

  // Update audio source when current audio changes
  useEffect(() => {
    if (state.currentAudio && audioRef.current) {
      // Use environment configuration to get the correct URL for the audio file
      // This will use frontend URL for local /Audio files and handle external URLs properly
      const src = getAssetUrl(state.currentAudio.filePath);

      // Only update source and reset flag if it's a different audio
      if (audioRef.current.src !== src) {
        restoredRef.current = false;
        audioRef.current.src = src;
        // Attach a one-time error listener for diagnostics
        const handleError = () => {
          dispatch({ type: 'SET_LOADING', payload: false });
        };
        audioRef.current.addEventListener('error', handleError, { once: true });
      }
    }
  }, [state.currentAudio]);

  // Handle play/pause based on isPlaying state
  useEffect(() => {
    if (audioRef.current) {
      if (state.currentAudio) {
        if (state.isPlaying) {
          audioRef.current.play().catch(err => {
            // Play failed, likely user interaction required
          });
        } else {
          audioRef.current.pause();
        }
      } else {
        // No current audio, stop and reset
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    }
  }, [state.isPlaying, state.currentAudio]);

  // Track play count when audio starts playing
  useEffect(() => {
    if (state.currentAudio?.id && state.isPlaying && trackedPlayRef.current !== state.currentAudio.id) {
      const audioId = state.currentAudio.id;
      trackedPlayRef.current = audioId;
      audioService.trackPlay(audioId)
        .then(response => {
          // Play count updated successfully
        })
        .catch(error => {
          // Failed to track play count
        });
    }
  }, [state.currentAudio?.id, state.isPlaying]);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      try {
        // Some mobile browsers don't support volume changes
        audioRef.current.volume = state.volume;
      } catch (error) {
        console.warn('Volume control not supported on this device:', error);
      }
    }
  }, [state.volume]);

  // Update document title when audio is playing
  useEffect(() => {
    if (state.currentAudio && state.isPlaying) {
      const artistName = state.currentAudio.naatKhawan?.name || 'Unknown Artist';
      document.title = `${state.currentAudio.title} - ${artistName}`;
    } else if (state.currentAudio && !state.isPlaying) {
      // When paused, still show the naat title
      const artistName = state.currentAudio.naatKhawan?.name || 'Unknown Artist';
      document.title = `${state.currentAudio.title} - ${artistName}`;
    } else {
      // Reset to default when no audio is loaded
      document.title = 'NaatCity - Islamic Audio Streaming Platform';
    }
  }, [state.currentAudio, state.isPlaying]);

  // Restore playback state on page load
  useEffect(() => {
    if (state.currentAudio && audioRef.current && !restoredRef.current) {
      const savedState = loadPlaybackStateFromStorage();

      // Only restore if the saved state is for the same audio
      const shouldRestore = savedState.audioId === state.currentAudio.id && savedState.currentTime > 0;

      console.log('Checking restoration:', {
        savedAudioId: savedState.audioId,
        currentAudioId: state.currentAudio.id,
        shouldRestore,
        savedTime: savedState.currentTime
      });

      if (shouldRestore) {
        const handleLoadedMetadata = () => {
          if (audioRef.current) {
            console.log('Audio metadata loaded, restoring position:', savedState.currentTime);

            // Set the saved time position
            audioRef.current.currentTime = savedState.currentTime;
            dispatch({ type: 'SET_CURRENT_TIME', payload: savedState.currentTime });

            // Resume playback if it was playing before refresh
            if (savedState.isPlaying) {
              console.log('Attempting to resume playback');
              audioRef.current.play()
                .then(() => {
                  console.log('Playback resumed successfully');
                  dispatch({ type: 'PLAY' });
                })
                .catch(err => {
                  // Auto-play may be blocked, user interaction required
                  console.warn('Auto-resume blocked:', err);
                });
            }
          }

          restoredRef.current = true;
          audioRef.current?.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };

        // Check if metadata is already loaded
        if (audioRef.current.readyState >= 1) {
          console.log('Metadata already loaded, restoring immediately');
          handleLoadedMetadata();
        } else {
          console.log('Waiting for metadata to load...');
          audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
        }
      } else {
        // Mark as restored even if we didn't restore anything
        restoredRef.current = true;
      }
    }
  }, [state.currentAudio]);

  const playAudio = useCallback((audio: AudioFile, playlist?: AudioFile[], source?: 'queue' | 'favorites' | 'regular') => {
    dispatch({ type: 'SET_AUDIO', payload: { audio, playlist, source } });
    dispatch({ type: 'PLAY' });
  }, []);

  const togglePlay = useCallback(() => {
    if (state.isPlaying) {
      dispatch({ type: 'PAUSE' });
    } else {
      dispatch({ type: 'PLAY' });
    }
  }, [state.isPlaying]);

  const pauseAudio = useCallback(() => {
    dispatch({ type: 'PAUSE' });
  }, []);

  const nextTrack = useCallback(() => {
    dispatch({ type: 'NEXT_TRACK' });
  }, []);

  const previousTrack = useCallback(() => {
    dispatch({ type: 'PREVIOUS_TRACK' });
  }, []);

  const setCurrentTime = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      dispatch({ type: 'SET_CURRENT_TIME', payload: time });
    }
  }, []);

  const setVolume = useCallback((volume: number) => {
    dispatch({ type: 'SET_VOLUME', payload: volume });
  }, []);

  const addToQueue = useCallback((audio: AudioFile): boolean => {
    // Check if already in queue
    const isAlreadyInQueue = state.queue.some(item => item.id === audio.id);
    if (!isAlreadyInQueue) {
      dispatch({ type: 'ADD_TO_QUEUE', payload: audio });
      return true; // Successfully added
    }
    return false; // Already in queue
  }, [state.queue]);

  const removeFromQueue = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_FROM_QUEUE', payload: index });
  }, []);

  const clearQueue = useCallback(() => {
    dispatch({ type: 'CLEAR_QUEUE' });
  }, []);

  const playNext = useCallback((audio: AudioFile) => {
    dispatch({ type: 'PLAY_NEXT', payload: audio });
  }, []);

  const toggleQueue = useCallback(() => {
    dispatch({ type: 'TOGGLE_QUEUE' });
  }, []);

  const stopPlayback = useCallback(() => {
    dispatch({ type: 'STOP_PLAYBACK' });
  }, []);

  const contextValue: AudioPlayerContextType = {
    state,
    playAudio,
    togglePlay,
    pauseAudio,
    nextTrack,
    previousTrack,
    setCurrentTime,
    setVolume,
    addToQueue,
    removeFromQueue,
    clearQueue,
    playNext,
    toggleQueue,
    stopPlayback,
    clearPlayHistory: () => {},
    playFromHistory: () => {},
  };

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

export default AudioPlayerProvider;